/**
 * InstanceStatusDialog — Change the status of a frozen instance.
 *
 * Supervisors can skip or cancel pending/in_progress instances.
 * Shows a confirmation with the new status and a brief explanation.
 */

import { useState, useEffect } from 'react';
import { useLanguage } from '@/core/LanguageContext';
import { useUpdateInstanceStatus } from '../hooks/useScheduleInstances';
import type { LpaScheduleInstanceWithDetails, LpaInstanceStatus } from '../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Label } from '@/shared/ui/label';
import { Loader2, SkipForward, Ban, CheckCircle2 } from 'lucide-react';

// ---------------------------------------------------------------------------
// Status transition rules
// ---------------------------------------------------------------------------

/** Allowed status transitions from each status. */
const ALLOWED_TRANSITIONS: Record<LpaInstanceStatus, LpaInstanceStatus[]> = {
  pending: ['skipped', 'cancelled'],
  in_progress: ['skipped', 'cancelled'],
  completed: [], // no transitions from completed
  overdue: ['skipped', 'cancelled'],
  skipped: ['pending'], // allow un-skip
  cancelled: ['pending'], // allow un-cancel
};

const STATUS_META: Record<
  LpaInstanceStatus,
  {
    labelKey: string;
    fallback: string;
    icon: React.ElementType;
    descKey: string;
    descFallback: string;
  }
> = {
  pending: {
    labelKey: 'mod_lpa.instances.status.pending',
    fallback: 'Pending',
    icon: CheckCircle2,
    descKey: 'mod_lpa.instances.statusDesc.pending',
    descFallback: 'Reset to pending — the auditor will need to complete this instance.',
  },
  in_progress: {
    labelKey: 'mod_lpa.instances.status.inProgress',
    fallback: 'In Progress',
    icon: CheckCircle2,
    descKey: 'mod_lpa.instances.statusDesc.inProgress',
    descFallback: '',
  },
  completed: {
    labelKey: 'mod_lpa.instances.status.completed',
    fallback: 'Completed',
    icon: CheckCircle2,
    descKey: 'mod_lpa.instances.statusDesc.completed',
    descFallback: '',
  },
  overdue: {
    labelKey: 'mod_lpa.instances.status.overdue',
    fallback: 'Overdue',
    icon: CheckCircle2,
    descKey: 'mod_lpa.instances.statusDesc.overdue',
    descFallback: '',
  },
  skipped: {
    labelKey: 'mod_lpa.instances.status.skipped',
    fallback: 'Skipped',
    icon: SkipForward,
    descKey: 'mod_lpa.instances.statusDesc.skipped',
    descFallback: 'Skip this instance — it will not be required for completion.',
  },
  cancelled: {
    labelKey: 'mod_lpa.instances.status.cancelled',
    fallback: 'Cancelled',
    icon: Ban,
    descKey: 'mod_lpa.instances.statusDesc.cancelled',
    descFallback: 'Cancel this instance — it will be marked as cancelled and removed from active view.',
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface InstanceStatusDialogProps {
  instance: LpaScheduleInstanceWithDetails | null;
  onClose: () => void;
  auditId: string;
}

export default function InstanceStatusDialog({
  instance,
  onClose,
  auditId,
}: InstanceStatusDialogProps) {
  const { t } = useLanguage();
  const updateStatus = useUpdateInstanceStatus();

  const [newStatus, setNewStatus] = useState<LpaInstanceStatus | ''>('');

  const allowedTransitions = instance
    ? ALLOWED_TRANSITIONS[instance.status] ?? []
    : [];

  // Reset selected status when dialog opens
  useEffect(() => {
    if (instance) {
      setNewStatus(allowedTransitions.length > 0 ? allowedTransitions[0] : '');
    }
  }, [instance]);

  const handleSubmit = () => {
    if (!instance || !newStatus) return;
    updateStatus.mutate(
      {
        instanceId: instance.id,
        auditId,
        scheduleId: instance.schedule_id,
        input: { status: newStatus as LpaInstanceStatus },
      },
      { onSuccess: () => onClose() },
    );
  };

  const selectedMeta = newStatus ? STATUS_META[newStatus as LpaInstanceStatus] : null;

  return (
    <Dialog open={!!instance} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>
            {t('mod_lpa.instances.statusDialog.title', 'Change Instance Status')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'mod_lpa.instances.statusDialog.description',
              'Update the status of this audit instance. This action is recorded.',
            )}
          </DialogDescription>
        </DialogHeader>

        {instance && (
          <div className="space-y-4 py-2">
            {/* Current info */}
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                {instance.schedule.name}
              </span>
              {' — '}
              {instance.auditor.full_name ?? instance.auditor.email}
              {' — '}
              {new Date(instance.period_start).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
              })}
              {'–'}
              {new Date(instance.period_end).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
              })}
            </div>

            {/* Current status */}
            <div className="flex items-center gap-2 text-sm">
              <Label className="text-muted-foreground w-24 shrink-0">
                {t('mod_lpa.instances.statusDialog.current', 'Current:')}
              </Label>
              <Badge variant="outline" className="text-xs">
                {t(
                  STATUS_META[instance.status].labelKey,
                  STATUS_META[instance.status].fallback,
                )}
              </Badge>
            </div>

            {/* New status selector */}
            {allowedTransitions.length > 0 ? (
              <div className="space-y-2">
                <Label>
                  {t('mod_lpa.instances.statusDialog.newStatus', 'New Status')}
                </Label>
                <Select
                  value={newStatus}
                  onValueChange={(val) => setNewStatus(val as LpaInstanceStatus)}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t('mod_lpa.instances.statusDialog.select', 'Select status...')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {allowedTransitions.map((status) => {
                      const meta = STATUS_META[status];
                      const Icon = meta.icon;
                      return (
                        <SelectItem key={status} value={status}>
                          <span className="flex items-center gap-2">
                            <Icon className="h-3.5 w-3.5" />
                            {t(meta.labelKey, meta.fallback)}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>

                {/* Description of selected status */}
                {selectedMeta && selectedMeta.descFallback && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t(selectedMeta.descKey, selectedMeta.descFallback)}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t(
                  'mod_lpa.instances.statusDialog.noTransitions',
                  'No status changes are available for this instance.',
                )}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={updateStatus.isPending}>
            {t('mod_lpa.instances.statusDialog.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!newStatus || updateStatus.isPending}
          >
            {updateStatus.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            {t('mod_lpa.instances.statusDialog.confirm', 'Update Status')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
